import { MediaTypes } from '@/types/enums'
import { CropSize, ImageUrlRequest, Point, Size } from '@/types/imageSizing'
import { chakra, Image, useBreakpointValue } from '@chakra-ui/react'
import { MediaData } from './MediaData'

export interface MediaImageProps extends MediaImageData {
  hasBorderRadius?: boolean
  cropSizeAlias?: Partial<Record<string, string>>
  viewportWidthFactor: Partial<Record<string, number>>
  imageUrlBuilder: (request: ImageUrlRequest) => string
  crop?: boolean
  isFullWidth?: boolean
}

export interface MediaImageData extends MediaData {
  type?: MediaTypes.Image
  imageSrc: string
  altText?: string
  cropSizes?: CropSize[]
  focalPoint?: Point
  originalSize?: Size
  bytes?: number
  isFullWidth?: boolean
  draggable?: boolean
  objectFit?: any
}

export default chakra(MediaImage)

const defaultCrop: CropSize = { width: 1920, height: 1080, alias: 'default' }

function MediaImage({
  type,
  imageSrc,
  altText,
  cropSizes,
  viewportWidthFactor = { base: 0.5 },
  hasBorderRadius = false,
  cropSizeAlias = { base: 'default' },
  imageUrlBuilder,
  originalSize,
  bytes,
  focalPoint,
  crop = true,
  isFullWidth = false,
  draggable = false,
  objectFit = 'cover',
  ...props
}: MediaImageProps) {
  // Evaluate responsives
  const cropSizeAliasValue = useBreakpointValue(cropSizeAlias)
  const viewportWidthFactorValue =
    useBreakpointValue(viewportWidthFactor) ?? 0.5

  // Pick crop size
  const cropSize =
    cropSizes?.find((c) => c.alias === cropSizeAliasValue) ?? defaultCrop

  // Determine the scaled breakpoint values
  const factors = [0.4, 0.75, 1.0]

  const scaleSize = (size: Size, factor: number) => ({
    width: size.width * factor,
    height: size.height * factor,
  })

  const normalizeToWidth = (cropSize: Size, width: number) => {
    const aspectRatio = cropSize.height / cropSize.width
    return { width: width, height: width * aspectRatio }
  }

  const sizes = factors.map((factor) =>
    scaleSize(normalizeToWidth(cropSize, 1920), factor),
  )

  // Determine sources to render
  const sources = sizes.map((size) => ({
    src: imageUrlBuilder
      ? imageUrlBuilder({
          imageSrc,
          cropSize,
          focalPoint,
          crop,
          size: scaleSize(size, viewportWidthFactorValue),
          originalSize,
        })
      : imageSrc,
    media: `(max-width: ${size.width.toFixed(0)}px)`,
  }))

  // Default src for browsers that don't support picture/sources/srcset (do we still get those..?)
  const src = sources[sources.length - 1].src

  return (
    <picture>
      {sources.map((source, index) => (
        <source key={index} media={source.media} srcSet={source.src} />
      ))}
      <Image
        src={src}
        alt={altText}
        width='full'
        height='full'
        objectFit={objectFit ?? 'cover'}
        borderRadius={
          isFullWidth ? '0 0.75rem 0.75rem 0' : hasBorderRadius ? 'xl' : 'none'
        }
        draggable={draggable}
        {...props}
      />
    </picture>
  )
}
